import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Grid, Box, Paper, IconButton } from '@material-ui/core';
import SettingsIcon from '@material-ui/icons/Settings';
import DeleteIcon from '@material-ui/icons/Delete';
import { nanoid } from 'nanoid';
import Draggable, { DraggableData, DraggableEvent } from 'react-draggable';
import useWindowSize from '@rehooks/window-size';

import { FontType } from '../redux/cardAttributeModelIndex';
import { CoordinateType, deepCopy } from '../general';
import { initial } from '../card_components/index';
import DrawToolsMenu from '../components/DrawToolsMenu';
import DrawTextFontDialog from '../components/DrawTextFontDialog';
import ColorPalette from './ColorPalette';

type DrawAreaPaperProps = {
  ratio: number;
  color: string;
  frameDisplay: boolean;
  frameColor: string;
}
const DrawAreaPaper = styled(Paper)<DrawAreaPaperProps>`
  width: ${props => 480 * props.ratio + 'px'};
  height: 480px;
  margin: 24px;
  background-color: ${props => props.color};
  border: ${props => props.frameDisplay ? 'solid ' + props.frameColor : undefined};
`
const DrawSvg = styled.svg`
  pointer-events: none;
`
type OmittableFontType = {[P in keyof FontType]?: FontType[P]};
type TextDivProps = {
  font: OmittableFontType;
}
const TextLine = styled.text`
  user-select: none;
`
const EditingInput = styled.textarea<TextDivProps>`
  width: 75%;
  pointer-events: all;
  font-size: ${props => props.font.size + 'px'};
  font-weight: ${props => props.font.bold ? 'bold' : 'normal'};
  color: ${props => props.font.color};
  font-style: ${props => props.font.italic ? 'italic' : 'normal'};
  text-decoration: ${props => props.font.underline ? 'underline' : undefined};
  font-family: ${props => props.font.name};
`

export type DrawSettingType = {
  mode: 'pen' | 'eraser' | 'move';
  penWidth: number;
  penColor: string;
  eraserWidth: number;
  backgroundColor: string;
  frameDisplay: boolean;
  frameColor: string;
}
export type MPointType = { cmd: 'M'; x: number; y: number; }
export type APointType = { cmd: 'A'; x: number; y: number; r: number; angle: number; sweep: 0 | 1; part: 'head' | 'tail'; }
export type LPointType = { cmd: 'L'; x: number; y: number; }
export type PointsType = (MPointType | APointType | LPointType)[]
export type ActionType = { width: number; fill: string; ds: string[]; pointsSet: PointsType[]; }
export type ActionsHashType = { [key in string]: ActionType }
type CircleSegmentType = { cmd: 'A'; start: CoordinateType; end: CoordinateType; angle: number; part: 'head' | 'tail'; }
type LineSegmentType = { cmd: 'L'; start: CoordinateType; end: CoordinateType; }
export type UndoneActionType = { id: string; fill: string; d: string; }
type DrawTextType = { text: string; x: number; y: number; font: FontType; }
export type DrawTextHashType = { [key in string]: DrawTextType; }

// const isCircleSegmentType = (obj: any): obj is CircleSegmentType => {
//   return typeof obj?.angle === 'number';
// }
// const isAPointType = (obj: MPointType | APointType | LPointType): obj is APointType => {
//   return obj.cmd === 'A';
// }

type DrawAreaProps = {
  kind: string;
  shadow: boolean;
  dataUrl?: string;
  setting: DrawSettingType;
  actionIds: string[];
  setActionIds: (ids: string[]) => void;
  actions: ActionsHashType;
  setActions: (actions: ActionsHashType) => void;
  initUndoneActions: () => void;
  textIds: string[];
  textHash: DrawTextHashType;
  setTextHash: (hash: DrawTextHashType) => void;
  editingTextId?: string;
  setEditingTextId: (id?: string) => void;
  onFont: (id: string, font: FontType) => void;
}
const DrawArea: React.FC<DrawAreaProps> = ({kind, shadow, dataUrl, setting, actionIds, setActionIds, actions, setActions, initUndoneActions, textIds, textHash, setTextHash, editingTextId, setEditingTextId, onFont}) => {
  const [drawnPosition, setDrawnPosition] = useState<CoordinateType | undefined>(undefined);
  const [drawing, setDrawing] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!editingTextId) return;
    const target = document.getElementById(editingTextId);
    target?.focus();
  }, [editingTextId])

  const sin = (rotation: number) => {
    return rotation % 180 === 0 ? 0 : Math.sin(Math.PI * rotation / 180);
  }
  const cos = (rotation: number) => {
    return (rotation - 90) % 180 === 0 ? 0 : Math.cos(Math.PI * rotation / 180);
  }
  const getDragHorizontalAngle = (x: number, y: number): number => {
    if (y === 0 && x === 0) return 0;
    if (x === 0 && y > 0) return 90;
    else if (x === 0 && y < 0) return 270;
    else if (y >= 0 && x > 0) return Math.atan(Math.abs(y) / Math.abs(x)) * 180 / Math.PI;
    else if (y >= 0 && x < 0) return 180 - Math.atan(Math.abs(y) / Math.abs(x)) * 180 / Math.PI;
    else if (y < 0 && x < 0) return Math.atan(Math.abs(y) / Math.abs(x)) * 180 / Math.PI + 180;
    else return 360 - Math.atan(Math.abs(y) / Math.abs(x)) * 180 / Math.PI;
  }
  // const getPointsDistance = (point1: CoordinateType, point2: CoordinateType) => {
  //   return Math.hypot(point1.x - point2.x, point1.y - point2.y);
  // }
  // const getPointCondition = (point: CoordinateType, circle: CircleSegmentType) => {
  //   const adjustment = circle.part === 'head' ? 2 : 0;
  //   const xConditions = [1 / point.x, point.x, point.x, 1 / point.x];
  //   const yConditions = [1 / point.y, 1 / point.y, point.y, point.y];
  //   return { x: xConditions[(circle.angle / 90) % 4 + adjustment], y: yConditions[(circle.angle / 90) % 4 + adjustment] };
  // }
  // const getCircleRange = (circle: CircleSegmentType) => {
  //   const adjustment = circle.part === 'head' ? 2 : 0;
  //   const xConditions = [1 / circle.start.x, circle.end.x, circle.start.x, 1 / circle.end.x];
  //   const yConditions = [1 / circle.end.y, 1 / circle.start.y, circle.end.y, circle.start.y];
  //   return { x: xConditions[(circle.angle / 90) % 4 + adjustment], y: yConditions[(circle.angle / 90) % 4 + adjustment] };
  // }
  // const isOnLineSegment = (point: CoordinateType, line: LineSegmentType) => {
  //   if (
  //     Math.min(line.start.x, line.end.x) <= point.x &&
  //     point.x <= Math.max(line.start.x, line.end.x) &&
  //     Math.min(line.start.y, line.end.y) <= point.y &&
  //     point.y <= Math.max(line.start.y, line.end.y)
  //   ) return true;
  //   else return false;
  // }
  // const getLineCoefficient = (linePoints: LineSegmentType): [number, number, number] => {
  //   const a = linePoints.end.y - linePoints.start.y;
  //   const b = linePoints.start.x - linePoints.end.x;
  //   const c = linePoints.end.x * linePoints.start.y - linePoints.start.x * linePoints.end.y;
  //   return [a, b, c];
  // }
  // const getCircleCoefficient = (circlePoints: CircleSegmentType): [number, number, number] => {
  //   const dx = circlePoints.end.x - circlePoints.start.x;
  //   const dy = circlePoints.end.y - circlePoints.start.y;
  //   const a = circlePoints.start.x + dx / 2;
  //   const b = circlePoints.start.y + dy / 2;
  //   const r = Math.sqrt(dx * dx + dy * dy) / 2;
  //   return [a, b, r];
  // }
  const getDValueByPoints = (points: PointsType) => {
    let dValue = '';
    points.forEach(point => {
      switch (point.cmd) {
        case 'M': dValue = dValue + ' M ' + point.x + ',' + point.y; break;
        case 'A': dValue = dValue + ' A ' + point.r + ' ' + point.r + ' ' + point.angle + ' 0 ' + point.sweep + ' ' + point.x + ',' + point.y; break;
        case 'L': dValue = dValue + ' L ' + point.x + ',' + point.y; break;
      }
    })
    return dValue;
  }
  // const getTracks = (points: PointsType) => {
  //   const tracks: (CircleSegmentType | LineSegmentType)[] = [];
  //   points.forEach((point, ix) => {
  //     if (ix < 1) return;
  //     const prePoint = points[ix - 1];
  //     switch (point.cmd) {
  //       case 'M': break;
  //       case 'A': tracks.push({ cmd: point.cmd, start: { x: prePoint.x, y: prePoint.y }, end: { x: point.x, y: point.y }, angle: point.angle, part: point.part }); break;
  //       case 'L': tracks.push({ cmd: point.cmd, start: { x: prePoint.x, y: prePoint.y }, end: { x: point.x, y: point.y } }); break;
  //     }
  //   })
  //   return tracks;
  // }
  // const calcCircleLineCrossPoint = (x: number, y: number, r: number, a: number, b: number, c: number) => {
  //   if (b === 0) return [];
  //   const m = -a / b;
  //   const n = -c / b;
  //   const o = 1 + Math.pow(m, 2);
  //   const p = -2 * x + 2 * m * (n - y);
  //   const q = Math.pow(x, 2) + Math.pow((n - y), 2) - Math.pow(r, 2);
  //   const D = Math.pow(p, 2) - 4 * o * q;
  
  //   const kouten = [];
  //   if (D >= 0) {
  //     const x1 = (-p + Math.sqrt(Math.pow(p, 2) - 4 * o * q)) / (2 * o);
  //     const x2 = (-p - Math.sqrt(Math.pow(p, 2) - 4 * o * q)) / (2 * o);
  //     if (D === 0) {
  //       //Dがゼロなら、1点だけが答え
  //       kouten.push({ x: x1, y: m * x1 + n});
  //     } else {
  //       //Dがゼロより上なら、2点が答え
  //       kouten.push({ x: x1, y: m * x1 + n});
  //       kouten.push({ x: x2, y: m * x2 + n});
  //     }
  //   }
  //   return kouten;
  // }
  // const get2LineCrossPoint = (line1Points: LineSegmentType, line2Points: LineSegmentType) => {
  //   const [a1, b1, c1] = getLineCoefficient(line1Points);
  //   const [a2, b2, c2] = getLineCoefficient(line2Points);

  //   if ((b1 !== 0 && b2 !== 0 && a1 / b1 === a2 / b2) || (b1 === 0 && b2 === 0)) return [];

  //   const x = (b1 * c2 - c1 * b2) / (a1 * b2 - b1 * a2);
  //   const y = (a1 * c2 - c1 * a2) / (b1 * a2 - a1 * b2);

  //   if (isOnLineSegment({x, y}, line1Points) && isOnLineSegment({x, y}, line2Points)) return [{ x: x, y: y }];
  //   else return [];
  // }
  // const getCircleLineCrossPoint = (circlePoints: CircleSegmentType, linePoints: LineSegmentType) => {
  //   const [x, y, r] = getCircleCoefficient(circlePoints);
  //   const [a, b, c] = getLineCoefficient(linePoints);
  //   const crossPoints = calcCircleLineCrossPoint(x, y, r, a, b, c);

  //   const isMeetingConditions = (point: CoordinateType) => {
  //     return getPointCondition(point, circlePoints).x >= getCircleRange(circlePoints).x &&
  //       getPointCondition(point, circlePoints).y >= getCircleRange(circlePoints).y &&
  //       isOnLineSegment(point, linePoints);
  //   }

  //   const resPoints: CoordinateType[] = [];
  //   crossPoints.forEach(point => {
  //     if (isMeetingConditions(point)) resPoints.push(point);
  //   })

  //   return resPoints;
  // }
  // const get2CircleCrossPoint = (circle1Points: CircleSegmentType, circle2Points: CircleSegmentType) => {
  //   const [x1, y1, r1] = getCircleCoefficient(circle1Points);
  //   const [x2, y2, r2] = getCircleCoefficient(circle2Points);
  //   const a = x1 - x2;
  //   const b = y1 - y2;
  //   const c = 0.5 * ((r1 - r2) * (r1 + r2) - a * (x1 + x2) - b * (y1 + y2));
  //   const crossPoints = calcCircleLineCrossPoint(x1, y1, r1, a, b, c);

  //   const isMeetingConditions = (point: CoordinateType) => {
  //     return getPointCondition(point, circle1Points).x >= getCircleRange(circle1Points).x &&
  //       getPointCondition(point, circle1Points).y >= getCircleRange(circle1Points).y &&
  //       getPointCondition(point, circle2Points).x >= getCircleRange(circle2Points).x &&
  //       getPointCondition(point, circle2Points).y >= getCircleRange(circle2Points).y;
  //   }

  //   const resPoints: CoordinateType[] = [];
  //   crossPoints.forEach(point => {
  //     if (isMeetingConditions(point)) resPoints.push(point);
  //   })

  //   return resPoints;
  // }
  const reDraw = (actionId: string, newAction?: ActionType) => {
    const svg = document.getElementById('svg');
    if (!svg) return;
    const drawnElements = document.getElementsByClassName(actionId);
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    let dValue = '';
    // 何故かfor文一回ではdrawnElementsが残ってしまうので、while文を併用する
    while (drawnElements.length > 0) {
      for (let i = 0; i < drawnElements.length; i++) {
        const target = drawnElements[i];
        if (setting.mode === 'eraser') {
          const targetD = target.getAttribute('d');
          dValue = dValue + targetD;
        }
        target.remove();
      }
    }

    type PrefixType = 'p:' | 'e:';
    const prefix = actionId.substring(0, 2) as PrefixType;
    // if (prefix === 'e:') return;

    const action = newAction ? newAction : actions[actionId];
    if (action) {

      action.pointsSet.forEach((points, ix) => {
        if (action.pointsSet.length > 1 && ix === 0) return;
        points.forEach(point => {
          switch (point.cmd) {
            case 'M': dValue = dValue + ' M ' + point.x + ',' + point.y; break;
            case 'A': dValue = dValue + ' A ' + point.r + ' ' + point.r + ' ' + point.angle + ' 0 ' + point.sweep + ' ' + point.x + ',' + point.y; break;
            case 'L': dValue = dValue + ' L ' + point.x + ',' + point.y; break;
          }
        })
      })
    }
    const penColor = action ? action.fill : setting.penColor;
    const colorHash = { 'p:': penColor, 'e:': setting.backgroundColor };
    const color = colorHash[prefix];
    path.setAttribute('id', actionId);
    path.setAttribute('class', actionId);
    path.setAttribute('fill', color);
    path.setAttribute('d', dValue);
    svg.appendChild(path);
  }
  // const eraserExe = (eraserPoints: PointsType) => {
  //   const deletePoints = eraserPoints.map(point => {
  //     if (isAPointType(point)) return { ...point, sweep: 1 as 0 | 1 };
  //     else return point;
  //   })
  //   const ids = Object.keys(actions);
  //   ids.forEach(id => {
  //     const action = actions[id];
  //     action.pointsSet.forEach((points, pointsIx) => {
  //       const penTracks = getTracks(points);
  //       const eraserTracks = getTracks(eraserPoints);

  //       const crossPoints: CoordinateType[] = [];
  //       console.log('########')
  //       penTracks.forEach((penTrack, penTrackIx) => {
  //         if (isCircleSegmentType(penTrack)) {
  //           eraserTracks.forEach((eraserTrack, eraserTrackIx) => {
  //             if (isCircleSegmentType(eraserTrack)) {
  //               const crossPoints = get2CircleCrossPoint(penTrack, eraserTrack);
  //               if (crossPoints.length === 2) {
  //                 const newActions = deepCopy(actions);
  //                 const targetPoints = newActions[id].pointsSet[pointsIx];
  //                 const penTarget = targetPoints[penTrackIx + 1];
  //                 const deleteTarget = deletePoints[eraserTrackIx + 1];

  //                 const dist0 = getPointsDistance(penTrack.start, crossPoints[0]);
  //                 const dist1 = getPointsDistance(penTrack.start, crossPoints[1]);
  //                 const crossPoint0 = dist0 < dist1 ? crossPoints[0] : crossPoints[1];
  //                 const crossPoint1 = dist0 < dist1 ? crossPoints[1] : crossPoints[0];
  //                 const addDValue: PointsType = [
  //                   { ...penTarget, x: crossPoint0.x, y: crossPoint0.y },
  //                   { ...deleteTarget, x: crossPoint1.x, y: crossPoint1.y },
  //                   penTarget
  //                 ]

  //                 targetPoints.splice(penTrackIx + 1, 1, ...addDValue);
  //                 setActions(newActions);
  //                 reDraw(id, newActions[id]);
  //               }
  //             }
  //             else {
  //               const crossPoints = getCircleLineCrossPoint(penTrack, eraserTrack);
  //             }
  //           })
  //         } else {
  //           eraserTracks.forEach((eraserTrack, eraserTrackIx) => {
  //             if (isCircleSegmentType(eraserTrack)) {
  //               const crossPoints = getCircleLineCrossPoint(eraserTrack, penTrack);
  //             }
  //             else {
  //               const crossPoints = get2LineCrossPoint(penTrack, eraserTrack);
  //             }
  //           })
  //         }
  //       })
  //       // if (crossPoints.length === 2) {
  //       //   const newActions = deepCopy(actions);
  //       //   const targetPoints = newActions[id].pointsSet[pointsIx];
  //       //   const penTarget = targetPoints[penTrackIx + 1];
  //       //   const deleteTarget = deletePoints[eraserTrackIx + 1];

  //       //   const dist0 = getPointsDistance(penTrack.start, crossPoints[0]);
  //       //   const dist1 = getPointsDistance(penTrack.start, crossPoints[1]);
  //       //   const crossPoint0 = dist0 < dist1 ? crossPoints[0] : crossPoints[1];
  //       //   const crossPoint1 = dist0 < dist1 ? crossPoints[1] : crossPoints[0];
  //       //   const addDValue: PointsType = [
  //       //     { ...penTarget, x: crossPoint0.x, y: crossPoint0.y },
  //       //     { ...deleteTarget, x: crossPoint1.x, y: crossPoint1.y },
  //       //     penTarget
  //       //   ]

  //       //   targetPoints.splice(penTrackIx + 1, 1, ...addDValue);
  //       //   setActions(newActions);
  //       //   reDraw(id, newActions[id]);
  //       // }
  //     })
  //   })
  // }
  const drawPen = (ev: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    const svg = document.getElementById('svg');
    if (!svg || !drawnPosition || !drawing) return;
    const action = actions[drawing];

    const originPosition: CoordinateType =
      { x: Math.trunc(ev.currentTarget.getClientRects()[0].left), y: Math.trunc(ev.currentTarget.getClientRects()[0].top) };
    const position: CoordinateType =
      { x: Math.trunc(ev.clientX) - originPosition.x, y: Math.trunc(ev.clientY) - originPosition.y };
    if (position === drawnPosition) return;

    const angle = getDragHorizontalAngle(position.x - drawnPosition.x, position.y - drawnPosition.y);
    const adjustWidthX = setting.penWidth / 2 * sin(angle);
    const adjustWidthY = setting.penWidth / 2 * -cos(angle);
    const points: PointsType = [
      { cmd: 'M', x: drawnPosition.x + adjustWidthX, y: drawnPosition.y + adjustWidthY },
      { cmd: 'A', x: drawnPosition.x - adjustWidthX, y: drawnPosition.y - adjustWidthY, r: action.width / 2, angle: angle, sweep: 0, part: 'tail' },
      { cmd: 'L', x: position.x - adjustWidthX, y: position.y - adjustWidthY },
      { cmd: 'A', x: position.x + adjustWidthX, y: position.y + adjustWidthY, r: action.width / 2, angle: angle, sweep: 0, part: 'head' },
      { cmd: 'L', x: drawnPosition.x + adjustWidthX, y: drawnPosition.y + adjustWidthY }
    ]
    const dValue = getDValueByPoints(points);
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('class', drawing);
    path.setAttribute('fill', setting.penColor);
    path.setAttribute('d', dValue);
    svg.appendChild(path);

    const newActions = deepCopy(actions);
    const target = newActions[drawing];
    target.ds.push(dValue);
    target.pointsSet.push(points);
    setActions(newActions);

    setDrawnPosition(position);
  }
  const drawEraser = (ev: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    const svg = document.getElementById('svg');
    if (!svg || !drawnPosition || !drawing) return;

    const originPosition: CoordinateType =
      { x: Math.trunc(ev.currentTarget.getClientRects()[0].left), y: Math.trunc(ev.currentTarget.getClientRects()[0].top) };
    const position: CoordinateType =
      { x: Math.trunc(ev.clientX) - originPosition.x, y: Math.trunc(ev.clientY) - originPosition.y };
    if (position === drawnPosition) return;

    const angle = getDragHorizontalAngle(position.x - drawnPosition.x, position.y - drawnPosition.y);
    const adjustWidthX = setting.eraserWidth / 2 * sin(angle);
    const adjustWidthY = setting.eraserWidth / 2 * -cos(angle);
    const eraserPoints: PointsType = [
      { cmd: 'M', x: drawnPosition.x + adjustWidthX, y: drawnPosition.y + adjustWidthY },
      { cmd: 'A', x: drawnPosition.x - adjustWidthX, y: drawnPosition.y - adjustWidthY, r: setting.eraserWidth / 2, angle: angle, sweep: 0, part: 'tail' },
      { cmd: 'L', x: position.x - adjustWidthX, y: position.y - adjustWidthY },
      { cmd: 'A', x: position.x + adjustWidthX, y: position.y + adjustWidthY, r: setting.eraserWidth / 2, angle: angle, sweep: 0, part: 'head' },
      { cmd: 'L', x: drawnPosition.x + adjustWidthX, y: drawnPosition.y + adjustWidthY }
    ]
    const dValue = getDValueByPoints(eraserPoints);
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('class', drawing);
    path.setAttribute('fill', setting.backgroundColor);
    path.setAttribute('d', dValue);
    svg.appendChild(path);

    // eraserExe(eraserPoints);

    setDrawnPosition(position);
  }
  const drawLine = (ev: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (setting.mode === 'pen') drawPen(ev);
    if (setting.mode === 'eraser') drawEraser(ev);
  }
  const drawStart = (ev: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    const svg = document.getElementById('svg');
    if (!svg || setting.mode === 'move') return;
    type PrefixType = 'p:' | 'e:';
    const drawnIdPrefixHash = { 'pen': 'p:', 'eraser': 'e:' };
    const prefix = drawnIdPrefixHash[setting.mode] as PrefixType;
    const originPosition: CoordinateType =
      { x: Math.trunc(ev.currentTarget.getClientRects()[0].left), y: Math.trunc(ev.currentTarget.getClientRects()[0].top) };
    const position: CoordinateType =
      { x: Math.trunc(ev.clientX) - originPosition.x, y: Math.trunc(ev.clientY) - originPosition.y };

    const newId = prefix + nanoid();
    const colorHash = { 'p:': setting.penColor, 'e:': setting.backgroundColor };
    const widthHash = { 'p:': setting.penWidth, 'e:': setting.eraserWidth };
    const color = colorHash[prefix];
    const width = widthHash[prefix];
    const points: PointsType = [
      { cmd: 'M', x: position.x, y: position.y - width / 2 },
      { cmd: 'A', x: position.x, y: position.y + width / 2, r: width / 2, angle: 0, sweep: 0, part: 'tail' },
      { cmd: 'A', x: position.x, y: position.y - width / 2, r: width / 2, angle: 0, sweep: 0, part: 'head' }
    ]
    const dValue = getDValueByPoints(points);
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('class', newId);
    path.setAttribute('fill', color);
    path.setAttribute('d', dValue);
    svg.appendChild(path);
  
    const newActionIds = deepCopy(actionIds);
    newActionIds.push(newId);
    setActionIds(newActionIds);

    if (setting.mode === 'pen') {
      const newActions = deepCopy(actions);
      newActions[newId] = { width: width, fill: color, ds: [dValue], pointsSet: [points] };
      setActions(newActions);
    }
    // else eraserExe(points);

    setDrawing(newId);
    setDrawnPosition(position);
    initUndoneActions();
  }
  const drawEnd = () => {
    if (drawing) reDraw(drawing);
    setDrawing(undefined);
    setDrawnPosition(undefined);
  }
  const dragText = (data: DraggableData, id: string) => {
    const newHash = deepCopy(textHash);
    const target = textHash[id];
    newHash[id] = { ...target, x: data.x, y: data.y };
    setTextHash(newHash);
  }
  const changeText = (id: string, value: string) => {
    const newHash = deepCopy(textHash);
    newHash[id].text = value;
    setTextHash(newHash);
  }
  const textEditStart = (id: string) => {
    setEditingTextId(id);
  }
  const onEditingInputBlur = (ev: React.FocusEvent<HTMLTextAreaElement>) => {
    const newTarget = ev.relatedTarget as EventTarget & HTMLTextAreaElement;
    if (newTarget && newTarget.className && newTarget.className.includes('text-menu')) return;
    else setEditingTextId(undefined);
  }
  const getTextLines = (text: string) => {
    const content = text.split('\n');
    return content;
  }
  const getRows = (text: string) => {
    const content = text.split('\n');
    return content.length;
  }
  const deleteText = (id: string) => {
    const newHash = deepCopy(textHash);
    delete newHash[id];
    setTextHash(newHash);
  }

  return (
    <DrawAreaPaper elevation={shadow ? 4 : 0} id="draw-area-paper"
      ratio={kind === 'card' ? initial.cardWidth / initial.cardHeight : 1}
      color={setting.backgroundColor}
      frameDisplay={setting.frameDisplay} frameColor={setting.frameColor}
      onMouseDown={(ev) => drawStart(ev)}
      onMouseMove={(ev) => drawLine(ev)}
      onMouseUp={() => drawEnd()}
      onMouseLeave={() => drawEnd()}
      onContextMenu={() => drawEnd()}
    >
      <DrawSvg id="svg" width={kind === 'card' ? String(480 * initial.cardWidth / initial.cardHeight) : '480'} height="480" xmlns="http://www.w3.org/2000/svg">
      { dataUrl && <image href={dataUrl} width={kind === 'card' ? String(480 * initial.cardWidth / initial.cardHeight) : '480'} height="480" /> }
      { textIds.map(id => {
        const item = textHash[id];
        if (!item) return undefined;
        const lines = getTextLines(item.text);
        const { name, color, size, bold, italic, underline } = item.font;
        return (
          <Draggable position={{x: item.x, y: item.y}}
            onDrag={(ev: DraggableEvent, data: DraggableData) => dragText(data, id)}
            disabled={setting.mode !== 'move' || id === editingTextId}
          >
            { id !== editingTextId ?
              <g x="0" y="0" style={{pointerEvents: 'auto'}} onDoubleClick={() => textEditStart(id)}>
                { lines.map((line, ix) => 
                  <TextLine x="0" y={ix * size * 1.5}
                    fontFamily={name} fill={color} fontSize={size} fontWeight={bold ? 'bold' : 'normal'}
                    fontStyle={italic ? 'italic' : 'normal'} textDecoration={underline ? 'underline' : undefined}
                  >
                    { line }
                  </TextLine>
                ) }
              </g>
            :
              <foreignObject x="-2" y={-size - 4} width="100%" height="100%">
                <EditingInput id={id} value={item.text}
                  rows={getRows(item.text)}
                  onChange={(ev) => changeText(id, ev.currentTarget.value)}
                  onBlur={(ev) => onEditingInputBlur(ev)}
                  font={{ name, color, size, bold, italic, underline }}
                />
                <div>
                  <IconButton className="text-menu" style={{pointerEvents: 'auto'}} onClick={() => onFont(id, item.font)}>
                    <SettingsIcon />
                  </IconButton>
                  <IconButton className="text-menu" style={{pointerEvents: 'auto'}} onClick={() => deleteText(id)}>
                    <DeleteIcon />
                  </IconButton>
                </div>
              </foreignObject>
            }
          </Draggable>
        );
      })}
      </DrawSvg>
    </DrawAreaPaper>
  );
}

export type DrawToolContentProps = {
  kind: string;
  shadow?: boolean;
  dataUrl?: string;
  setting: DrawSettingType;
  setSetting: (setting: DrawSettingType) => void;
  actionIds: string[];
  setActionIds: (ids: string[]) => void
  actions: ActionsHashType;
  setActions: (actions: ActionsHashType) => void;
  undoneActions: UndoneActionType[];
  setUndoneActions: (actions: UndoneActionType[]) => void;
  textHash: DrawTextHashType;
  setTextHash: (hash: DrawTextHashType) => void;
}
const DrawToolContent: React.FC<DrawToolContentProps> = ({kind, shadow, dataUrl, setting, setSetting, actionIds, setActionIds, actions, setActions, undoneActions, setUndoneActions, textHash, setTextHash}) => {
  const windowSize = useWindowSize()
  const workHeight = windowSize.innerHeight - (64 * 2) - 130
  const textIds = Object.keys(textHash);
  const [editingTextId, setEditingTextId] = useState<string>();
  type FontActionType = { show: boolean; id?: string; font?: FontType }
  const [fontAction, setFontAction] = useState<FontActionType>({ show: false });

  const changeBackgroundColor = (value: string) => {
    actionIds.forEach(id => {
      if (id.substring(0, 2) !== 'e:') return;
      const action = document.getElementById(id);
      if (!action) return;
      action.setAttribute('stroke', value);
      action.setAttribute('fill', value);
    })
    setSetting({...setting, backgroundColor: value});
  }
  const undoAction = () => {
    const svg = document.getElementById('svg');
    const ix = actionIds.length - 1;
    const targetId = actionIds[ix];
    const target = document.getElementById(targetId);
    if (!svg || !target) return;
    const fill = target.getAttribute('fill');
    const d = target.getAttribute('d');
    if (!fill || !d) return;
    target.remove();
    const newUndoneActions = deepCopy(undoneActions);
    newUndoneActions.push({ id: targetId, fill: fill, d: d });
    setUndoneActions(newUndoneActions);
    const newActionIds = deepCopy(actionIds);
    newActionIds.splice(ix, 1);
    setActionIds(newActionIds);
  }
  const redoAction = () => {
    const svg = document.getElementById('svg');
    if (!svg) return;
    const ix = undoneActions.length - 1;
    const target = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    let fill = undoneActions[ix].fill;
    if (undoneActions[ix].id.substring(0, 2) === 'e:') {
      fill = setting.backgroundColor;
    }
    target.setAttribute('id', undoneActions[ix].id);
    target.setAttribute('fill', fill);
    target.setAttribute('d', undoneActions[ix].d);
    svg.appendChild(target);
    const newActionIds = deepCopy(actionIds);
    newActionIds.push(undoneActions[ix].id);
    setActionIds(newActionIds);
    const newUndoneActions = deepCopy(undoneActions);
    newUndoneActions.splice(ix, 1);
    setUndoneActions(newUndoneActions);
  }
  const addText = () => {
    const newTextHash = deepCopy(textHash);
    const newId = 't:' + nanoid();
    newTextHash[newId] = { text: '入力してください', x: 4, y: 100, font: initial.font };
    setTextHash(newTextHash);
    setEditingTextId(newId);
  }
  const changeFont = (id: string | undefined, font: FontType) => {
    if (!id) return;
    const newHash = deepCopy(textHash);
    newHash[id].font = font;
    setTextHash(newHash);
  }

  return(
    <Box>
      <Grid container spacing={0}>
        <Grid item xs={12}>
          <Grid container spacing={0}>
            <Grid item xs={2}>
              <Box style={{height: workHeight, overflow: 'auto'}}>
                <DrawToolsMenu setting={setting} setSetting={(setting: DrawSettingType) => setSetting(setting)}
                  actionIdsLength={actionIds.length} undoneActionsLength={undoneActions.length}
                  undoAction={undoAction} redoAction={redoAction} addText={addText}
                />
              </Box>
            </Grid>
            <Grid item xs={10}>
              <Grid container>
                <Grid item xs={12}>
                  <DrawArea kind={kind} shadow={(shadow !== undefined) ? shadow : true} dataUrl={dataUrl}
                    setting={setting} actionIds={actionIds}
                    setActionIds={setActionIds}
                    actions={actions} setActions={setActions}
                    initUndoneActions={() => setUndoneActions([])}
                    textIds={textIds} textHash={textHash}
                    setTextHash={setTextHash}
                    editingTextId={editingTextId} setEditingTextId={setEditingTextId}
                    onFont={(id, font) => setFontAction({ show: true, id, font })}
                  />
                </Grid>
                { kind !== 'card' &&
                  <Grid item xs={12}>
                    <ColorPalette
                      currentValue={setting.backgroundColor}
                      onChange={changeBackgroundColor}
                      label="背景色"
                    />
                  </Grid>
                }
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      { fontAction.font &&
        <DrawTextFontDialog open={fontAction.show}
          onClose={() => {
            setFontAction({ show: false });
            setEditingTextId(undefined);
          }}
          fontValue={fontAction.font}
          changeFont={(font) => changeFont(fontAction.id, font)}
        />
      }
    </Box>
  );
}

export default DrawToolContent;
